import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * Enable flex layout on the children slot. Must be set for any of the gap, direction, or
   * alignment props to take effect.
   *
   * @type {Boolean|String}
   * @default false
   */
  flex: booleanAttribute(),
  /**
   * Flex wrap behaviour of children — "wrap" allows children to flow onto multiple lines, "nowrap"
   * keeps them on a single line.
   *
   * @type {String}
   * @storybook-control select
   * @default "wrap"
   */
  flexWrap: {
    type: String,
    validator: (value) => ["wrap", "nowrap"].includes(value),
    default: "wrap",
  },
  /**
   * Row gap applied between children. Accepts a number (rem) or any CSS length string.
   *
   * @type {String|Number}
   * @default "0"
   */
  rowgap: {
    type: [String, Number],
    default: "0",
  },
  /**
   * Row gap applied below the small breakpoint. Falls back to rowgap when not set.
   *
   * @type {String|Number}
   * @default undefined
   */
  rowgapSm: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Column gap applied between children. Accepts a number (rem) or any CSS length string.
   *
   * @type {String|Number}
   * @default "0"
   */
  columngap: {
    type: [String, Number],
    default: "0",
  },
  /**
   * Column gap applied below the small breakpoint. Falls back to columngap when not set.
   *
   * @type {String|Number}
   * @default undefined
   */
  columngapSm: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Flex direction at the default breakpoint — row or column.
   *
   * @type {String}
   * @storybook-control select
   * @default "row"
   */
  direction: {
    type: String,
    default: "row",
    validator: (value) => ["row", "column"].includes(value),
  },
  /**
   * Flex direction applied below the small breakpoint. Defaults to column so row layouts stack on
   * mobile.
   *
   * @type {String}
   * @storybook-control select
   * @default "column"
   */
  directionSm: {
    type: String,
    default: "column",
    validator: (value) => ["row", "column"].includes(value),
  },
  /**
   * Cross-axis alignment of children.
   *
   * @type {String}
   * @storybook-control select
   * @default "normal"
   */
  alignItems: {
    type: String,
    default: "normal",
    validator: (value) => ["normal", "start", "end", "center", "stretch"].includes(value),
  },
  /**
   * Main-axis distribution of children.
   *
   * @type {String}
   * @storybook-control select
   * @default "normal"
   */
  justifyContent: {
    type: String,
    default: "normal",
    validator: (value) => ["normal", "start", "end", "center", "stretch"].includes(value),
  },
});
